// Free brain-test pages (2026-10-31). Every rule, timing and threshold of the
// tests belongs to the cognitive service: the pages read it from the public
// API (/free_tests/<test>/config, through the gateway) and send each run to
// /free_tests/<test>/score for the result. Nothing here may hard-code a test
// value (one owner per value). No sign-up is needed. The service keeps each
// scored run to build score comparisons, with the kind of input used (see
// `deviceClass`) and, since 2026-11-03, the context the gateway adds on its
// own: IP address and country, the analytics visitor id and first-touch
// source (from the site's cookie), and the account when the visitor is signed
// in to the web app in this browser (routers/free_tests/router.py).

import { rememberResult } from "./accountProgress";

export type FreeTestId =
  | "focus_sound"
  | "focus_sounds"
  | "hearing"
  | "reaction_time"
  | "attention_span"
  | "go_no_go"
  | "n_back"
  | "creativity"
  | "matrix"
  | "number_memory"
  | "stroop"
  | "visual_memory"
  | "face_memory"
  | "emotion_recognition"
  | "sequence_memory"
  | "chimp"
  | "verbal_memory"
  | "mental_math"
  | "mental_rotation"
  | "trail_making"
  | "symbol_digit"
  | "iq"
  | "two_thirds"
  | "balloon_risk"
  | "iowa_gambling"
  | "tower_of_london"
  | "sense_of_direction"
  | "typing_speed"
  | "click_speed"
  | "aim_trainer"
  | "big_five"
  | "imagery"
  | "color_perception"
  | "musical_ear"
  | "perfect_circle"
  | "time_perception"
  | "daily_puzzle"
  | "reading_speed"
  // Brain-area games, set G (2026-11-04)
  | "vocabulary"
  | "task_switching"
  | "knowledge_quiz"
  | "remote_associates"
  | "social_shapes"
  | "decision_traps"
  | "brain_health"
  | "stress_resilience";

export interface ReactionTimeConfig {
  test: "reaction_time";
  practice_trials: number;
  counted_trials: number;
  min_delay_ms: number;
  max_delay_ms: number;
  feedback_ms: number;
  anticipation_ms: number;
  lapse_ms: number;
  lapse_inclusive: boolean;
}

export interface AttentionSpanConfig {
  test: "attention_span";
  duration_ms: number;
  min_isi_ms: number;
  max_isi_ms: number;
  feedback_ms: number;
  timeout_ms: number;
  anticipation_ms: number;
  lapse_ms: number;
  lapse_inclusive: boolean;
}

export interface GoNoGoConfig {
  test: "go_no_go";
  total_trials: number;
  no_go_ratio: number;
  stimulus_ms: number;
  isi_ms: number;
  feedback_ms: number;
  practice_sequence: boolean[];
}

export interface NBackConfig {
  test: "n_back";
  start_n: number;
  min_n: number;
  block_scored_trials: number;
  block_targets: number;
  min_blocks: number;
  max_blocks: number;
  stimulus_ms: number;
  isi_ms: number;
  feedback_ms: number;
  level_change_ms: number;
  advance_hit_rate: number;
  advance_false_alarm_rate: number;
  drop_hit_rate: number;
  practice_sequence: { cell: number; is_target: boolean }[];
}

export interface CreativityConfig {
  test: "creativity";
  target_words: number;
  score_words: number;
  min_word_length: number;
  max_word_length: number;
}

export interface MatrixConfig {
  test: "matrix";
  items_per_test: number;
  confirm_ms: number;
}

// The six web-only games (2026-11-03). The page runs the game with these
// values and sends the whole trial log; the service computes the result.
export interface NumberMemoryConfig {
  test: "number_memory";
  start_digits: number;
  max_digits: number;
  tries_per_length: number;
  show_base_ms: number;
  show_per_digit_ms: number;
}

export interface StroopConfig {
  test: "stroop";
  colors: string[];
  /** The colour words in the page's language (code -> word, 2026-11-04); missing from older services. */
  color_words?: Record<string, string>;
  /** The keyboard letter for each colour in the page's language, when the words' letters work as keys. */
  keys?: Record<string, string> | null;
  practice_trials: number;
  counted_trials: number;
  congruent_ratio: number;
  timeout_ms: number;
  isi_ms: number;
  feedback_ms: number;
}

export interface VisualMemoryConfig {
  test: "visual_memory";
  start_tiles: number;
  max_level: number;
  grid_steps: { from_level: number; side: number }[];
  show_ms: number;
  pause_ms: number;
  misses_per_round: number;
  lives: number;
}

export interface SequenceMemoryConfig {
  test: "sequence_memory";
  grid_side: number;
  start_length: number;
  max_length: number;
  tries_per_length: number;
  flash_ms: number;
  gap_ms: number;
}

export interface ChimpConfig {
  test: "chimp";
  start_numbers: number;
  max_numbers: number;
  grid_cols: number;
  grid_rows: number;
  strikes: number;
}

export interface VerbalMemoryConfig {
  test: "verbal_memory";
  lives: number;
  seen_ratio: number;
  min_seen: number;
  max_trials: number;
}

// The four seeded games (2026-11-03, batch 3). Each run's items come from
// /free_tests/<test>/items with a seed; the page sends the seed back with the
// answers, and the service rebuilds the items from it to score them.
export interface MentalMathConfig {
  test: "mental_math";
  duration_ms: number;
  ops: MathOp[];
  add_range: [number, number];
  mul_range: [number, number];
}

export interface MentalRotationConfig {
  test: "mental_rotation";
  practice_trials: number;
  counted_trials: number;
  angles: number[];
  mirror_ratio: number;
  shape_cells: number;
  timeout_ms: number;
  isi_ms: number;
  feedback_ms: number;
}

export interface TrailMakingConfig {
  test: "trail_making";
  circles: number;
  sample_circles: number;
  box_w: number;
  box_h: number;
  radius: number;
  part_limit_ms: number;
}

export interface SymbolDigitConfig {
  test: "symbol_digit";
  duration_ms: number;
  symbols: number;
  glyphs: number;
  practice_items: number;
  feedback_ms: number;
}

// Two planning and navigation games (2026-11-04, strategy games B): seeded
// like the four above; the service replays the moves / the walk to score them.
export interface TowerOfLondonConfig {
  test: "tower_of_london";
  /** Balls each peg holds, left to right. */
  capacities: number[];
  practice_problems: number;
  problems: number;
  /** The fewest moves of each counted problem, easy to hard. */
  levels: number[];
  max_moves: number;
  problem_limit_ms: number;
  style_min_moves: number;
  style_plan_share: number;
}

/** A position: per peg, colour indexes (0–2) from bottom to top. */
export type TowerPosition = number[][];

export interface TowerProblem {
  start: TowerPosition;
  goal: TowerPosition;
  min_moves: number;
}

export interface TowerOfLondonItems {
  seed: number;
  practice: TowerProblem[];
  problems: TowerProblem[];
}

export interface TowerOfLondonResult {
  n_problems: number;
  n_perfect: number;
  n_solved: number;
  extra_moves: number;
  mean_planning_ms: number | null;
  mean_total_ms: number | null;
  plan_share_hard: number | null;
  style: "planner" | "step_by_step" | null;
  by_level: {
    min_moves: number;
    n: number;
    n_perfect: number;
    n_solved: number;
    mean_planning_ms: number | null;
    mean_total_ms: number | null;
  }[];
  compare?: PlayerComparison[];
}

export interface SenseOfDirectionConfig {
  test: "sense_of_direction";
  levels: number;
  checkpoints: number[];
  map_ms: number[];
  time_limit_ms: number[];
  /** Tiles per second. */
  speed: number;
  turn_deg_per_s: number;
  player_radius: number;
  reach_radius: number;
  view_radius: number;
  sample_ms: number;
}

/** One maze: rows of '#' (hedge) and '.' (open); positions in tiles, y down; heading in compass degrees. */
export interface NavLevel {
  tiles: string[];
  start: [number, number];
  heading: number;
  checkpoints: [number, number][];
  rooms_w: number;
  rooms_h: number;
  map_ms: number;
  time_limit_ms: number;
  fog: boolean;
}

export interface SenseOfDirectionItems {
  seed: number;
  practice: NavLevel;
  levels: NavLevel[];
}

export interface SenseOfDirectionResult {
  n_levels: number;
  n_finished: number;
  route_efficiency_pct: number;
  pointing_error_deg: number | null;
  levels: {
    finished: boolean;
    checkpoints: number;
    reached: number;
    efficiency: number;
    time_ms: number;
    point_error_deg: number | null;
  }[];
  compare?: PlayerComparison[];
}

/**
 * Where a result stands among other players' first runs (cognitive
 * public_test_compare.py). `beat_pct` is null until `min_players` have played.
 */
export interface PlayerComparison {
  metric: string;
  n_players: number;
  min_players: number;
  beat_pct: number | null;
  /** below / typical / above the middle of players (bands in the service); null with beat_pct. */
  band: "below" | "typical" | "above" | null;
  /** True when the page may show beat_pct; otherwise it shows the band (the service decides, e.g. from how many have played). */
  exact: boolean;
  /** Players needed before the exact share shows (null = always a band). */
  exact_min_players: number | null;
}

// Speed & skill games (2026-11-04). Results are compared only within one
// input (keyboard / phone keyboard, mouse / touch / spacebar) and mode.
export interface TypingSpeedConfig {
  test: "typing_speed";
  durations_ms: number[];
  default_duration_ms: number;
  chars_per_word: number;
  /** The language of the passages served for this page (English where a language has none). */
  text_language: string;
  passages_version: string;
  compare_min_players: number;
}

export interface ClickSpeedConfig {
  test: "click_speed";
  durations_ms: number[];
  default_duration_ms: number;
  modes: ("click" | "space")[];
  /** Above this many presses a second (per input) a run counts as a likely auto-clicker. */
  max_cps: Record<"mouse" | "touch" | "keyboard", number>;
  compare_min_players: number;
}

export interface AimTrainerConfig {
  test: "aim_trainer";
  targets: number;
  sizes: number[];
  min_jump: number;
  hit_slack: number;
  run_limit_ms: number;
  compare_min_players: number;
}

export interface TypingItems {
  seed: number;
  text_language: string;
  text: string;
}

/** Target centre and diameter in units of the square play area's side. */
export interface AimTarget {
  x: number;
  y: number;
  d: number;
}

export interface AimItems {
  seed: number;
  targets: AimTarget[];
}

/**
 * Where a result stands among other players' first runs with the same input and mode
 * (cognitive public_test_skill_games.py; the other games use PlayerComparison /
 * public_test_compare.py: two helpers for now, merge note 2026-11-04).
 */
export interface SkillComparison {
  metric: string;
  device: string;
  n_players: number;
  min_players: number;
  /** Share of those players this result beats (ties half); null until min_players. */
  beat_pct: number | null;
}

export interface SkillQuality {
  flags: string[];
}

export interface TypingSpeedResult {
  duration_ms: number;
  wpm: number;
  raw_wpm: number;
  accuracy: number | null;
  n_words: number;
  n_words_correct: number;
  n_words_wrong: number;
  correct_chars: number;
  uncorrected_errors: number;
  text_language: string;
  counted: boolean;
  quality: SkillQuality;
  compare: SkillComparison | null;
}

export interface ClickSpeedResult {
  mode: "click" | "space";
  input: "mouse" | "touch" | "keyboard";
  duration_ms: number;
  clicks: number;
  cps: number;
  best_second: number;
  per_second: number[];
  mean_interval_ms: number | null;
  counted: boolean;
  quality: SkillQuality;
  compare: SkillComparison | null;
}

export interface AimTrainerResult {
  n_targets: number;
  n_misses: number;
  accuracy: number;
  mean_ms: number;
  median_ms: number;
  fastest_ms: number;
  throughput_bps: number | null;
  fitts_a_ms: number | null;
  fitts_b_ms_per_bit: number | null;
  by_size: { size: number; n: number; mean_ms: number | null }[];
  points: { id: number; ms: number }[];
  counted: boolean;
  quality: SkillQuality;
  compare: SkillComparison | null;
}

// Senses and hype games (2026-11-04). The page runs each game with these
// values and sends its whole log; the service computes the result (it
// replays the adaptive staircases and refuses a log the rules can't produce).
export interface ColorPerceptionConfig {
  test: "color_perception";
  /** OKLCH lightness and chroma of every colour (only the hue changes). */
  lightness: number;
  chroma: number;
  boundary: {
    hue_min: number;
    hue_max: number;
    /** Start hue of the two staircases: [from the green end, from the blue end]. */
    starts: [number, number];
    start_step: number;
    min_step: number;
    trials_per_staircase: number;
    gap_ms: number;
  };
  order: {
    rows: { start: number; end: number; caps: number[] }[];
    /** Hue of each cap round the circle (cap number = index). */
    cap_hues: number[];
    n_rows: number;
    caps_per_row: number;
    n_caps: number;
    hue_step: number;
  };
  hue_bands: { from: number; band: string }[];
}

export interface MusicalEarConfig {
  test: "musical_ear";
  sound: { harmonics: number[]; tone_ms: number; fade_ms: number; gap_ms: number; volume: number };
  pitch: {
    base_hz: [number, number];
    start_cents: number;
    max_cents: number;
    min_cents: number;
    step_start: number;
    step_fine: number;
    reversals: number;
    used_reversals: number;
    max_trials: number;
    practice_cents: number[];
    feedback_ms: number;
  };
  rhythm: {
    unit_ms: number;
    patterns: number[][];
    plays: number;
    play_gap_units: number;
    n_patterns: number;
    tap_timeout_ms: number;
    match_error: number;
  };
  notes: { rounds: number; midi: [number, number]; gap_ms: number; semitone_credit: number };
}

export interface PerfectCircleConfig {
  test: "perfect_circle";
  min_points: number;
  max_points: number;
  /** Smallest mean radius, as a share of the drawing area's shorter side. */
  min_radius_ratio: number;
  min_sweep_deg: number;
  max_sweep_deg: number;
  /** Largest gap between the line's ends, as a share of the radius. */
  close_gap_ratio: number;
  max_draw_ms: number;
  /** Word for a roundness: the first grade whose min_pct the roundness reaches (highest first). */
  grades: { min_pct: number; grade: CircleGrade }[];
}

export type CircleGrade = "perfect" | "great" | "good" | "wobbly";

export interface TimePerceptionConfig {
  test: "time_perception";
  targets_ms: number[];
  n_rounds: number;
  first_target_ms: number;
  min_press_ms: number;
  max_factor: number;
  /** A round within this many % of the target reads "spot on". */
  spot_on_pct: number;
  /** An average signed error within this many % reads "neither early nor late". */
  even_bias_pct: number;
}

export interface ColorPerceptionResult {
  boundary_hue: number;
  boundary_side: "inside" | "below" | "above" | "all_blue" | "all_green";
  boundary_n_trials: number;
  boundary_n_blue: number;
  order_error: number;
  order_row_errors: number[];
  order_n_caps: number;
  order_n_misplaced: number;
  order_cap_scores: number[];
  order_worst_band: string | null;
  order_ms: number | null;
}

export interface MusicalEarResult {
  pitch_threshold_cents: number | null;
  pitch_threshold_pct: number | null;
  pitch_n_trials: number;
  pitch_n_correct: number;
  pitch_n_reversals: number;
  pitch_smallest_right_cents: number | null;
  rhythm_n_patterns: number;
  rhythm_n_matched: number;
  rhythm_n_wrong_count: number;
  rhythm_error_pct: number | null;
  rhythm_tempo_ratio: number | null;
  rhythm_patterns: { pattern: number; taps: number; error_pct: number | null }[];
  notes_n_rounds: number;
  notes_n_exact: number;
  notes_n_semitone: number;
  notes_score_pct: number | null;
}

export type CircleReason = "too_short" | "too_small" | "not_closed" | "too_many_loops" | "too_slow";

export interface PerfectCircleResult {
  valid: boolean;
  reason: CircleReason | null;
  roundness_pct: number | null;
  grade: CircleGrade | null;
  radius_px: number | null;
  radius_rel: number | null;
  sweep_deg: number | null;
  clockwise: boolean | null;
  draw_ms: number;
  n_points: number;
}

export interface TimePerceptionResult {
  mean_abs_error_pct: number;
  mean_error_pct: number;
  best_error_pct: number;
  best_target_ms: number;
  n_rounds: number;
  rounds: { target_ms: number; produced_ms: number; error_pct: number; timed_out: boolean }[];
}

// Helibrain Daily (2026-11-04): one number-grid puzzle per UTC day, the same
// for everyone; the puzzle and today's numbers come from /items, the result
// from replaying the entries on the server.
export interface DailyPuzzleConfig {
  test: "daily_puzzle";
  epoch: string;
  week: { size: number; level: number }[];
  min_size: number;
  max_size: number;
  stats_min_players: number;
}

export interface DailyPuzzleToday {
  number: number;
  n_solved: number;
  median_time_ms: number | null;
  min_players: number;
}

export interface DailyPuzzleItems {
  test: "daily_puzzle";
  number: number;
  date: string;
  weekday: number;
  size: number;
  level: number;
  givens: { cell: number; value: number }[];
  /** The sign between `cell` and its right ("r") or lower ("d") neighbour; gt: `cell` is the bigger one. */
  signs: { cell: number; dir: "r" | "d"; gt: boolean }[];
  seconds_to_next: number;
  today: DailyPuzzleToday | null;
}

export interface DailyPuzzleResult {
  number: number;
  date: string;
  size: number;
  level: number;
  solved: boolean;
  time_ms: number | null;
  mistakes: number;
  row_mistakes: number[];
  n_moves: number;
  today: DailyPuzzleToday | null;
}

// Reading speed (2026-11-04): an original passage per language from /items,
// questions afterwards; the rate counts only with enough right answers.
export type ReadingUnit = "words" | "characters";

export interface ReadingSpeedConfig {
  test: "reading_speed";
  unit: ReadingUnit;
  passage_locale: string;
  questions: number;
  options: number;
  min_correct: number;
  max_rate: number;
  min_read_ms: number;
  passages: number;
  passage_length_min: number;
  passage_length_max: number;
  reference: {
    source: string;
    fiction_wpm: number;
    fiction_range: [number, number];
    nonfiction_wpm: number;
    nonfiction_range: [number, number];
  } | null;
}

export interface ReadingSpeedItems {
  test: "reading_speed";
  seed: number;
  passage_id: string;
  title: string;
  paragraphs: string[];
  length: number;
  unit: ReadingUnit;
  questions: { q: string; options: string[] }[];
}

export interface ReadingSpeedResult {
  passage_id: string;
  version: string;
  unit: ReadingUnit;
  length: number;
  read_ms: number;
  rate: number | null;
  counted: boolean;
  not_counted_reason: "too_fast" | "comprehension" | null;
  n_questions: number;
  n_correct: number;
  min_correct: number;
}

export type MathOp = "add" | "sub" | "mul" | "div";

export interface MentalMathItems {
  seed: number;
  problems: { a: number; op: MathOp; b: number }[];
}

/** One shape pair: cells as [x, y] (y down); draw `right` turned by `angle` degrees (clockwise > 0). */
export interface RotationView {
  left: [number, number][];
  right: [number, number][];
  angle: number;
  mirror?: boolean; // practice only (feedback)
}

export interface MentalRotationItems {
  seed: number;
  practice: RotationView[];
  trials: RotationView[];
}

export interface TrailCircle {
  label: string;
  x: number;
  y: number;
}

export interface TrailMakingItems {
  seed: number;
  parts: { part: "A" | "B"; sample: TrailCircle[]; circles: TrailCircle[] }[];
}

export interface SymbolDigitItems {
  seed: number;
  key: number[]; // key[d - 1] = symbol id for digit d
  practice: number[];
  items: number[];
}

export interface MentalMathResult {
  score: number;
  n_answered: number;
  n_wrong: number;
  n_add: number;
  n_sub: number;
  n_mul: number;
  n_div: number;
  ms_per_correct: number | null;
}

export interface MentalRotationResult {
  n_trials: number;
  n_correct: number;
  accuracy: number;
  n_timeouts: number;
  mean_rt_correct_ms: number | null;
  by_angle: { angle: number; n: number; n_correct: number; mean_rt_correct_ms: number | null }[];
}

export interface TrailMakingResult {
  time_a_ms: number;
  time_b_ms: number;
  b_minus_a_ms: number;
  b_over_a: number | null;
  errors_a: number;
  errors_b: number;
}

export interface SymbolDigitResult {
  score: number;
  n_answered: number;
  n_errors: number;
  ms_per_correct: number | null;
}

export interface SpanResult {
  span: number;
  longest_tried: number;
  n_trials: number;
  n_correct: number;
}

export interface StroopResult {
  n_trials: number;
  n_correct: number;
  accuracy: number;
  n_timeouts: number;
  n_match: number;
  n_mismatch: number;
  n_correct_match: number;
  n_correct_mismatch: number;
  mean_rt_match_ms: number | null;
  mean_rt_mismatch_ms: number | null;
  mismatch_cost_ms: number | null;
}

export interface VisualMemoryResult {
  level: number;
  tiles: number;
  n_rounds: number;
  lives_lost: number;
}

export interface ChimpResult {
  numbers: number;
  n_rounds: number;
  strikes: number;
}

export interface VerbalMemoryResult {
  score: number;
  n_words: number;
  n_mistakes: number;
  n_unique_words: number;
}

export interface MatrixItem {
  item_id: string;
  image_ref: string;
  answers_image_ref: string | null;
  n_options: number;
}

export interface MatrixResult {
  n_items: number;
  n_correct: number;
  accuracy: number;
}

// The IQ-style test (2026-11-03): five sections, one /items call per run and
// one /score call at the end (the cognitive service's iq_test.py owns every
// rule, item and norm).
export type IqSection = "reasoning" | "working_memory" | "speed" | "spatial" | "verbal";
export type IqNorms = "omib_offset" | "omib_group" | "own" | null;

export interface IqConfig {
  test: "iq";
  version: string;
  sections: { code: IqSection; title: string; norms: IqNorms }[];
  reasoning: { items: number; practice: number; item_limit_ms: number; elements: number };
  working_memory: {
    forward_start: number;
    forward_max: number;
    backward_start: number;
    backward_max: number;
    tries_per_length: number;
    show_base_ms: number;
    show_per_digit_ms: number;
  };
  speed: { duration_ms: number; symbols: number; glyphs: number; practice_items: number; feedback_ms: number };
  spatial: { items: number; practice: number; timeout_ms: number; isi_ms: number; feedback_ms: number };
  verbal: { items: number };
  age_bands: string[];
  age_no_comparison: string[];
  display_min: number;
  display_max: number;
  practice_gain_points: number;
  reference: { group: string; group_n: number; description: string } | null;
  /**
   * True when the page's language has no verbal (word) items yet (2026-11-04):
   * `sections` then leaves out "verbal", and the result has no verbal part.
   */
  verbal_skipped?: boolean;
}

/** A construction puzzle (8 cells drawn; build the 9th) or a multiple-choice item. */
export type IqItem =
  | { id: string; format: "construction"; cells: string[] }
  | { id: string; format: "rotation"; left: [number, number][]; right: [number, number][]; angle: number }
  | {
      id: string;
      format: "choice";
      kind: "number_series" | "letter_series" | "synonym" | "analogy" | "odd_one_out";
      options: string[];
      terms?: (string | number)[];
      word?: string;
      pair?: [string, string];
      stem?: string;
    };

export interface IqItems {
  test: "iq";
  version: string;
  seed: number;
  sections: {
    reasoning: { practice: { id: string; cells: string[]; solution: string }[]; items: IqItem[] };
    working_memory: { forward: Record<string, string[]>; backward: Record<string, string[]> };
    speed: { key: number[]; practice: number[]; items: number[] };
    spatial: { practice: (RotationView & { mirror: boolean })[]; items: IqItem[] };
    verbal: { items: IqItem[] };
  };
}

export interface IqComparison {
  kind: "iq" | "group_percentile" | "raw";
  mid?: number;
  low?: number;
  high?: number;
  low_capped?: boolean;
  high_capped?: boolean;
}

export interface IqResult {
  version: string;
  age_band: string | null;
  first_attempt: boolean | null;
  reasoning: { n_items: number; n_correct: number; theta: number; theta_se: number; norms: IqNorms; comparison: IqComparison };
  working_memory: { forward_span: number; backward_span: number; n_trials: number; n_correct: number; norms: IqNorms };
  speed: SymbolDigitResult & { norms: IqNorms };
  spatial: { n_items: number; n_correct: number; accuracy: number; mean_rt_correct_ms: number | null; norms: IqNorms };
  /** null when the page's language has no verbal section (config.verbal_skipped). */
  verbal: { n_items: number; n_correct: number; norms: IqNorms } | null;
  overall: IqComparison | null;
  overall_available: boolean;
  quality: Record<string, unknown>;
}

// The decision games (2026-11-04): guess 2/3 of the average (a weekly round),
// the balloon game (BART) and the card game (open Iowa Gambling Task variant).
// Rules, payoffs, bands and published reference numbers come from the
// cognitive service's config; results and comparisons are computed there.
export interface TwoThirdsConfig {
  test: "two_thirds";
  p_num: number;
  p_den: number;
  min: number;
  max: number;
  max_target: number;
  min_players: number;
  /** Level k (null = game theory's equilibrium) and its value. */
  ladder: { level: number | null; value: number }[];
  /** Published contests to compare a guess with (names and sources: page text). */
  published: { id: string; year: number; players: number | null; average: number; target: number; winner: number | null }[];
}

export type TwoThirdsLevel = { kind: "level"; level: number } | { kind: "equilibrium" | "above_max"; level: null };

export interface TwoThirdsRoundResult {
  id: string;
  starts_at: string;
  ends_at: string;
  status: "published" | "too_few";
  players: number;
  min_players?: number;
  average?: number;
  target?: number;
  winning_guesses?: number[];
  n_winners?: number;
  histogram?: number[];
  rank_by_guess?: number[];
}

export interface TwoThirdsState {
  test: "two_thirds";
  round: { id: string; starts_at: string; ends_at: string; players: number };
  last: TwoThirdsRoundResult;
}

export type TwoThirdsResult = TwoThirdsLevel & {
  round_id: string;
  round_ends_at: string;
  guess: number;
  counted: boolean;
  players: number;
  published: { id: string; target: number; distance: number; as_close_as_winner?: boolean }[];
};

export interface BalloonConfig {
  test: "balloon_risk";
  balloons: number;
  max_pumps: number;
  points_per_pump: number;
  best_fixed_pumps: number;
  type_bands: { below: number | null; type: string }[];
  compare_min_players: number;
  /** Published lab average (Lejuez et al. 2002), for a rough comparison. */
  reference?: { id: string; n: number; adjusted_mean: number; adjusted_sd: number };
}

export interface BalloonItems {
  seed: number;
  bursts: number[];
}

/** Where a score sits among earlier players (null: no comparison possible). */
export type PlayersComparison =
  | { available: true; n: number; below: number; equal: number; percentile: number }
  | { available: false; n: number; min_players: number }
  | null;

export interface BalloonResult {
  adjusted_pumps: number | null;
  total_points: number;
  n_popped: number;
  n_collected: number;
  total_pumps: number;
  by_third: (number | null)[];
  type: string;
  comparison: PlayersComparison;
}

export interface CardsConfig {
  test: "iowa_gambling";
  decks: number;
  picks: number;
  start_points: number;
  cards_per_deck: number;
  score_block: number;
  block_cards: number;
  kinds: Record<string, { win: number; n_losses: number; loss_total: number; net: number; good: boolean }>;
  compare_min_players: number;
  /** Healthy adults in published studies of this version (open data pool): net score per block. */
  reference?: { id: string; n: number; studies: number; blocks: number[]; net_mean: number; net_median: number; share_negative: number };
}

export interface CardsItems {
  seed: number;
  /** The cards of each on-screen deck, in order. */
  decks: { win: number; loss: number }[][];
}

export interface CardsResult {
  net_score: number;
  blocks: number[];
  final_points: number;
  points_change: number;
  n_good: number;
  n_bad: number;
  picks_by_kind: Record<string, number>;
  /** The kind (A–D) of each on-screen deck, revealed after the run. */
  deck_kinds: string[];
  comparison: PlayersComparison;
}

// Questionnaires (2026-11-04): the Big Five personality test and the mind's-eye
// check. /items gives the statements and the answer scale in the page's
// language (the scoring key stays in the service); /score gets one answer
// (1 … scale_points) per statement and returns the profile.
export type BigFiveTrait = "openness" | "conscientiousness" | "extraversion" | "agreeableness" | "emotional_stability";
export type ImagerySense = "visual" | "sound" | "smell" | "taste" | "touch";

interface QuestionnaireConfigBase {
  items: number;
  scale_points: number;
  version: string;
  /** False when the page's language has no statements yet (they come in English). */
  translated: boolean;
}

export interface BigFiveConfig extends QuestionnaireConfigBase {
  test: "big_five";
  traits: BigFiveTrait[];
  /** The published sample the percentiles compare with, or null (no percentiles). */
  norms: { sample: string; n: number; source: string; url?: string } | null;
}

export interface ImageryConfig extends QuestionnaireConfigBase {
  test: "imagery";
  visual_items: number;
  other_items: number;
  band_faint_max: number;
  band_vivid_min: number;
}

export interface QuestionnaireItems {
  test: "big_five" | "imagery";
  version: string;
  locale: string;
  instructions: string;
  scale: string[];
  items: { id: string; text: string; sense?: ImagerySense }[];
}

export interface BigFiveResult {
  version: string;
  n_items: number;
  traits: { trait: BigFiveTrait; mean: number; score: number; percentile: number | null }[];
}

export type ImageryBand = "none" | "faint" | "typical" | "very_vivid";

export interface ImageryResult {
  version: string;
  n_items: number;
  visual: number;
  other: number | null;
  senses: Partial<Record<ImagerySense, number>>;
  band: ImageryBand;
}

// Brain-area games, set G (2026-11-04): vocabulary size, task switching, and
// two daily games (knowledge quiz, three-word riddles). The cognitive service's
// public_test_area_games.py owns every rule and all the words.
export interface VocabularyConfig {
  mode?: "two_step";
  test: "vocabulary";
  word_locale: string;
  list_version: string;
  list_size: number;
  words_per_band: number;
  bands: number;
  real_words: number;
  pseudowords: number;
  items: number;
  practice_items: number;
  feedback_ms: number;
  max_false_alarm_rate: number;
  range_z: number;
  reference: { source: string; age: number; mean: number; low: number; high: number } | null;
}

export interface VocabularyItems {
  seed: number;
  list_version: string;
  practice: { text: string; real: boolean }[];
  items: string[];
}

export interface VocabularyResult {
  list_locale: string;
  list_size: number;
  estimate: number;
  range_low: number;
  range_high: number;
  n_words: number;
  n_hits: number;
  n_pseudowords: number;
  n_false_alarms: number;
  false_alarm_rate: number;
  accuracy_pct: number | null;
  reliable: boolean;
  n_fast: number;
  by_band: { rank_from: number; rank_to: number; n: number; n_yes: number; known_pct: number }[];
  fooled_by: string[];
}

export type SwitchTask = "color" | "shape";
export type SwitchBlockKind = SwitchTask | "mixed";

export interface TaskSwitchingConfig {
  test: "task_switching";
  colors: string[];
  shapes: string[];
  practice: { kind: SwitchBlockKind; trials: number }[];
  blocks: { kind: SwitchBlockKind; trials: number }[];
  counted_trials: number;
  mixed_trials: number;
  switch_ratio: number;
  cue_ms: number;
  timeout_ms: number;
  isi_ms: number;
  feedback_ms: number;
  min_rt_ms: number;
  break_ms: number;
}

/** color / shape = index of the key (0 = left, 1 = right) that colour or shape belongs to. */
export interface SwitchTrial {
  task: SwitchTask;
  color: 0 | 1;
  shape: 0 | 1;
}

export interface TaskSwitchingItems {
  seed: number;
  practice: { kind: SwitchBlockKind; trials: SwitchTrial[] }[];
  blocks: { kind: SwitchBlockKind; trials: SwitchTrial[] }[];
}

export interface TaskSwitchingResult {
  correct_per_min: number | null;
  correct_per_min_pure: number | null;
  n_mixed: number;
  n_correct_mixed: number;
  accuracy_mixed: number | null;
  accuracy_pure: number | null;
  accuracy_switch: number | null;
  accuracy_repeat: number | null;
  n_timeouts: number;
  mean_rt_switch_ms: number | null;
  mean_rt_repeat_ms: number | null;
  mean_rt_pure_ms: number | null;
  switch_cost_ms: number | null;
  mixing_cost_ms: number | null;
}

/** "Today's players" of a daily game (average and beat_pct only from min_players on). */
export interface TodayPlayers {
  n_players: number;
  min_players: number;
  average: number | null;
  beat_pct: number | null;
}

export interface KnowledgeQuizConfig {
  test: "knowledge_quiz";
  questions: number;
  question_ms: number;
  question_locale: string;
  bank_size: number;
  scheduled_days: number;
  epoch: string;
  compare_min_players: number;
}

export interface KnowledgeQuizItems {
  day: number;
  date: string;
  version: string;
  question_locale: string;
  questions: { id: string; topic: string; q: string; options: string[] }[];
}

export interface KnowledgeQuizResult {
  day: number;
  n_questions: number;
  n_correct: number;
  total_ms: number;
  marks: boolean[];
  review: { id: string; correct_index: number; fact: string | null }[];
  today: TodayPlayers | null;
}

export interface RemoteAssociatesConfig {
  test: "remote_associates";
  word_locale: string;
  per_day: number;
  time_ms: number;
  strip_chars: string;
  max_guesses: number;
  bank_size: number;
  scheduled_days: number;
  epoch: string;
  compare_min_players: number;
}

export interface RemoteAssociatesItems {
  day: number;
  date: string;
  version: string;
  word_locale: string;
  riddles: { id: string; cues: string[]; hashes: string[] }[];
}

export interface RemoteAssociatesResult {
  day: number;
  word_locale: string;
  n_riddles: number;
  n_solved: number;
  solve_ms: (number | null)[];
  mean_solve_ms: number | null;
  answers: { id: string; answer: string; solutions: string[] }[];
  today: TodayPlayers | null;
}

// Builder H: all items, rules and scoring are owned by public_test_profiles.py.
export type ProfileTestId = "social_shapes" | "decision_traps" | "brain_health" | "stress_resilience";
export interface ProfileConfig {
  test: ProfileTestId;
  locale: string;
  version: string;
  items: number;
  allow_skip: boolean;
  confidence_options: number[];
  preview_topics: number;
  min_profile_answers: number;
}
export interface ProfileItem {
  id: string;
  text: string;
  options: string[];
  confidence: boolean;
  video?: string;
  duration_ms?: number;
  label?: string;
  fact?: string;
}
export interface ProfileItems {
  test: ProfileTestId;
  version: string;
  instructions: string;
  items: ProfileItem[];
}
export interface ProfileAnswer { id: string; value: number | null; confidence: number | null; rt_ms: number; watched: boolean }
export interface HealthTopic { id: string; label: string; fact: string }
export interface ProfileResult {
  result_id?: string;
  version?: string;
  calm_mean?: number | null;
  n_items: number;
  n_answered?: number;
  n_correct?: number;
  decision_score?: number;
  response_band?: "less_calm" | "mixed" | "more_calm" | null;
  groups?: Record<"not_reported" | "explore" | "unsure" | "skipped", HealthTopic[]>;
  preview?: HealthTopic[];
  review?: { id: string; text: string; explanation: string; chosen: string; answer: string | null }[];
  compare?: PlayerComparison[];
}
export async function getProfileItems(test: ProfileTestId): Promise<ProfileItems> {
  return getItems<ProfileItems>(test, "The questions could not load.");
}

export type FaceTestId = "face_memory" | "emotion_recognition";

interface FaceConfigBase {
  bank_version: string;
  n_trials: number;
  gap_ms: number;
  image_load_timeout_ms: number;
  response_max_ms: number;
}
export interface FaceMemoryConfig extends FaceConfigBase {
  test: "face_memory";
  learn_count: number;
  learn_ms: number;
  n_options: number;
  stages: string[];
}
export interface EmotionRecognitionConfig extends FaceConfigBase {
  test: "emotion_recognition";
  labels: string[];
  intensities: string[];
}
export type FaceConfig = FaceMemoryConfig | EmotionRecognitionConfig;
interface FaceItemsBase { seed: number; bank_version: string }
export interface FaceMemoryItems extends FaceItemsBase {
  test: "face_memory";
  learn: string[];
  trials: { stage: string; options: string[] }[];
}
export interface EmotionRecognitionItems extends FaceItemsBase {
  test: "emotion_recognition";
  trials: { image: string }[];
}
export type FaceItems = FaceMemoryItems | EmotionRecognitionItems;
export interface FaceBreakdown { id: string; n_correct: number; n_trials: number; accuracy_pct: number }
export interface FaceResult {
  test: FaceTestId;
  bank_version: string;
  n_correct: number;
  n_trials: number;
  accuracy_pct: number;
  by_stage?: FaceBreakdown[];
  by_category?: FaceBreakdown[];
  by_intensity?: FaceBreakdown[];
  compare?: PlayerComparison[];
}

export interface FreeTestConfigs {
  focus_sound: import("./sound/types").FocusConfig;
  focus_sounds: import("./sound/types").PlayerConfig;
  hearing: import("./sound/types").HearingConfig;
  social_shapes: ProfileConfig;
  decision_traps: ProfileConfig;
  brain_health: ProfileConfig;
  stress_resilience: ProfileConfig;
  face_memory: FaceMemoryConfig;
  emotion_recognition: EmotionRecognitionConfig;
  reaction_time: ReactionTimeConfig;
  attention_span: AttentionSpanConfig;
  go_no_go: GoNoGoConfig;
  n_back: NBackConfig;
  creativity: CreativityConfig;
  matrix: MatrixConfig;
  number_memory: NumberMemoryConfig;
  stroop: StroopConfig;
  visual_memory: VisualMemoryConfig;
  sequence_memory: SequenceMemoryConfig;
  chimp: ChimpConfig;
  verbal_memory: VerbalMemoryConfig;
  mental_math: MentalMathConfig;
  mental_rotation: MentalRotationConfig;
  trail_making: TrailMakingConfig;
  symbol_digit: SymbolDigitConfig;
  tower_of_london: TowerOfLondonConfig;
  sense_of_direction: SenseOfDirectionConfig;
  iq: IqConfig;
  two_thirds: TwoThirdsConfig;
  balloon_risk: BalloonConfig;
  iowa_gambling: CardsConfig;
  typing_speed: TypingSpeedConfig;
  click_speed: ClickSpeedConfig;
  aim_trainer: AimTrainerConfig;
  big_five: BigFiveConfig;
  imagery: ImageryConfig;
  color_perception: ColorPerceptionConfig;
  musical_ear: MusicalEarConfig;
  perfect_circle: PerfectCircleConfig;
  time_perception: TimePerceptionConfig;
  daily_puzzle: DailyPuzzleConfig;
  reading_speed: ReadingSpeedConfig;
  vocabulary: VocabularyConfig;
  task_switching: TaskSwitchingConfig;
  knowledge_quiz: KnowledgeQuizConfig;
  remote_associates: RemoteAssociatesConfig;
}

export interface PvtResult {
  result_id?: string;
  /** Only returned for the opt-in time-of-day flow. */
  median_rt_ms?: number | null;
  input_device?: string;
  n_trials: number;
  n_valid: number;
  n_false_starts: number;
  n_lapses: number;
  mean_rt_ms: number | null;
  best_rt_ms: number | null;
  worst_rt_ms: number | null;
  reaction_times_ms: number[];
  tap_kinds: ("ok" | "slow" | "early")[];
}

export interface GoNoGoResult {
  n_trials: number;
  n_go: number;
  n_no_go: number;
  n_go_hits: number;
  n_omission_errors: number;
  n_commission_errors: number;
  n_correct_rejections: number;
  mean_go_rt_ms: number | null;
  accuracy: number | null;
}

export interface NBackResult {
  n_trials: number;
  n_targets: number;
  n_hits: number;
  n_false_alarms: number;
  max_n: number;
  end_n: number;
  hit_rate: number | null;
  false_alarm_rate: number | null;
  d_prime: number | null;
}

export interface CreativityResult {
  n_words: number;
  words: string[];
  skipped_words: { word: string; reason: string; message: string | null }[];
  extra_words: string[];
  dat_score: number | null;
}

export interface WordCheck {
  word: string;
  valid: boolean;
  reason: string | null;
  message: string | null;
}

import { track } from "@/lib/analytics";
import { interactions } from "@/lib/testInteractions";
import { scoreBucket } from "@/lib/challenge";
import { embedAttribution, isEmbedPage } from "@/lib/embed";

// Server side only: the gateway on loopback (hb_infra's helibrain-nextjs unit
// sets FASTAPI_URL, as for lib/blog.ts).
const API_BASE = (process.env.FASTAPI_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");

/**
 * The runner parameters of one test, read while the page renders (cached by
 * the gateway). null when the service can't be reached: the page still
 * renders its text, and the test card says it is unavailable.
 */
export async function getFreeTestConfig<T extends FreeTestId>(
  test: T,
  locale: string = "en",
): Promise<FreeTestConfigs[T] | null> {
  try {
    const res = await fetch(`${API_BASE}/free_tests/${test}/config?locale=${encodeURIComponent(locale)}`, {
      cache: "no-store",
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) {
      console.warn(`free test config ${test}: HTTP ${res.status}`);
      return null;
    }
    return (await res.json()) as FreeTestConfigs[T];
  } catch (err) {
    console.warn(`free test config ${test}: unreachable`, err);
    return null;
  }
}

/**
 * Why a call to the test service failed: `offline` (no connection), `busy`
 * (rate limit), `scoring` (a score call failed), `loading` (items didn't
 * load). The page shows the words for `code` in its language
 * (common.error_<code>); `message` is the English text for logs.
 */
export type FreeTestErrorCode = "offline" | "busy" | "scoring" | "loading";

export class FreeTestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: FreeTestErrorCode = "scoring",
  ) {
    super(message);
  }
}

// Browser side: the language of this page (set by components/i18n/I18nProvider).
// Sent with every item, word-check and score call (the cognitive service picks
// its words and items by it and keeps it with the stored result), and added to
// every free_test_* analytics event.
let testLocale = "en";

export function setTestLocale(locale: string): void {
  testLocale = locale;
}

export function currentTestLocale(): string {
  return testLocale;
}

/** A free_test_* analytics event, with the page's language. */
export function trackTest(
  event: string,
  props: Record<string, string | number | boolean | null | undefined>,
  beacon = false,
): void {
  track(event, { ...props, locale: testLocale }, beacon);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      credentials: isEmbedPage() ? "omit" : "same-origin",
      // Same origin, so the browser sends this site's cookies. The CSRF header
      // lets the gateway recognise a signed-in web app session (it attaches the
      // account to the stored result; nothing else changes for the visitor).
      headers: { "Content-Type": "application/json", "X-Helibrain-CSRF": "1" },
      body: JSON.stringify({ ...(body as Record<string, unknown>), ...embedAttribution() }),
    });
  } catch {
    throw new FreeTestError("We couldn't reach the server. Check your connection and try again.", 0, "offline");
  }
  if (res.status === 429) {
    throw new FreeTestError("Too many tries from this network for now. Please try again later.", 429, "busy");
  }
  if (!res.ok) throw new FreeTestError("Something went wrong while scoring. Please try again.", res.status, "scoring");
  return (await res.json()) as T;
}

// Browser side: which input the visitor actually responded with since the
// last scored run (tap = touch or pen, mouse click, or Space/Enter), so stored
// reaction times can be compared within one kind of input. Window capture
// listeners run before the runners' own handlers, so nothing is missed.
export type DeviceClass = "touch" | "mouse" | "keyboard";
const inputCounts: Record<DeviceClass, number> = { touch: 0, mouse: 0, keyboard: 0 };

if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (e) => {
      if (e.pointerType === "mouse") inputCounts.mouse += 1;
      else if (e.pointerType === "touch" || e.pointerType === "pen") inputCounts.touch += 1;
    },
    true,
  );
  window.addEventListener(
    "keydown",
    (e) => {
      if (!e.repeat && (e.code === "Space" || e.key === " " || e.key === "Enter")) inputCounts.keyboard += 1;
    },
    true,
  );
}

/** The input used most since the last scored run, or "unknown" if none was seen. */
function deviceClass(): DeviceClass | "unknown" {
  const ranked = (Object.keys(inputCounts) as DeviceClass[]).sort((a, b) => inputCounts[b] - inputCounts[a]);
  return inputCounts[ranked[0]] > 0 ? ranked[0] : "unknown";
}

// Browser side: how many runs of each test this browser has finished (for the
// attempt number in analytics, and to start returning visitors without
// practice). localStorage only; private windows or blocked storage just count
// from zero.
const RUNS_KEY = "hb_free_test_runs";

function readRuns(): Record<string, number> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RUNS_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Finished runs of `test` in this browser (0 on the server or without storage). */
export function finishedRuns(test: FreeTestId): number {
  if (typeof window === "undefined") return 0;
  const n = Number(readRuns()[test]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function noteFinishedRun(test: FreeTestId): number {
  const runs = readRuns();
  const n = finishedRuns(test) + 1;
  try {
    runs[test] = n;
    window.localStorage.setItem(RUNS_KEY, JSON.stringify(runs));
  } catch {
    // storage blocked: the count just isn't kept
  }
  return n;
}

// Browser side: the score challenge this page was opened with (a shared score
// link), set by components/tests/Challenge.tsx. Runs of that test carry
// `challenge` (+ the friend's score bucket) in analytics and `from_challenge`
// in the stored result.
let activeChallenge: { test: FreeTestId; score: number | null } | null = null;

export function setActiveChallenge(c: { test: FreeTestId; score: number | null } | null): void {
  activeChallenge = c;
}

function challengeProps(test: FreeTestId): Record<string, string | boolean> {
  if (!activeChallenge || activeChallenge.test !== test) return {};
  return activeChallenge.score === null
    ? { challenge: true }
    : { challenge: true, friend_score_bucket: scoreBucket(test, activeChallenge.score) };
}

/**
 * Browser side: a visitor started a run (analytics `free_test_started`).
 * `practiceSkipped`: only for tests with a practice round; true when no
 * practice was done before this run on this page.
 */
export function trackTestStarted(test: FreeTestId, practiceSkipped?: boolean): void {
  interactions.start(test);
  trackTest("free_test_started", {
    test,
    attempt: finishedRuns(test) + 1,
    practice_skipped: practiceSkipped,
    ...challengeProps(test),
  });
}

/** The result's plain fields (numbers, booleans): what the visitor was shown. No lists, no words. */
function scoreFields(result: unknown): Record<string, number | boolean | null> {
  const out: Record<string, number | boolean | null> = {};
  if (!result || typeof result !== "object") return out;
  for (const [k, v] of Object.entries(result as Record<string, unknown>)) {
    if (typeof v === "number" || typeof v === "boolean" || v === null) out[k] = v;
  }
  return out;
}

/**
 * Browser side: score one finished run (same origin: Caddy sends /free_tests
 * to the gateway). `practice_skipped` in the payload (tests with practice) is
 * kept with the stored result too, and so is `from_challenge` (a run on a
 * page opened from a shared score link). Sends `free_test_completed`.
 */
export async function scoreFreeTest<R>(
  test: FreeTestId,
  payload: Record<string, unknown> & { practice_skipped?: boolean },
  /** The input the page knows was used (typing: physical vs phone keyboard), instead of the counted one. */
  inputDevice?: DeviceClass,
  /** Optional collection context; standalone calls remain unchanged. */
  flow?: { flow: "workout" | "brain_profile"; flow_date: string; flow_run_id: string },
): Promise<R> {
  const device = inputDevice ?? deviceClass();
  const challenge = challengeProps(test);
  const out = await postJson<R>(`/free_tests/${test}/score`, {
    ...payload,
    ...flow,
    ...interactions.score(test),
    locale: testLocale,
    device,
    ...(challenge.challenge ? { from_challenge: true } : {}),
  });
  if (payload.flow === "time_of_day" && out && typeof out === "object") Object.assign(out, { input_device: device });
  if (!isEmbedPage() && out && typeof out === "object" && "result_id" in out) rememberResult(out.result_id);
  interactions.complete(test);
  inputCounts.touch = inputCounts.mouse = inputCounts.keyboard = 0; // the next run counts afresh
  const attempt = noteFinishedRun(test);
  // Collection scores stay in the scoring service and browser history.
  // Self-report and vocabulary fields remain excluded from analytics.
  if (!flow) trackTest("free_test_completed", {
    ...(payload.flow === "time_of_day" || payload.flow === "breathing_experiment" || test === "brain_health" || test === "stress_resilience" || test === "vocabulary" || test === "hearing" || test === "focus_sound" ? {} : scoreFields(out)),
    test,
    attempt,
    device,
    practice_skipped: payload.practice_skipped,
    ...challenge,
  });
  return out;
}

async function getItems<T>(test: FreeTestId, failMessage: string, seed?: number | null): Promise<T> {
  let res: Response;
  const seedParam = seed === undefined || seed === null ? "" : `&seed=${encodeURIComponent(String(seed))}`;
  try {
    res = await fetch(`/free_tests/${test}/items?locale=${encodeURIComponent(testLocale)}${seedParam}`, { cache: "no-store", credentials: isEmbedPage() ? "omit" : "same-origin" });
  } catch {
    throw new FreeTestError("We couldn't reach the server. Check your connection and try again.", 0, "offline");
  }
  if (res.status === 429) {
    throw new FreeTestError("Too many tries from this network for now. Please try again later.", 429, "busy");
  }
  if (!res.ok) throw new FreeTestError(failMessage, res.status, "loading");
  return (await res.json()) as T;
}

export function getFaceItems(test: FaceTestId, seed?: number | null): Promise<FaceItems> {
  return getItems<FaceItems>(test, "The faces couldn't load. Please try again.", seed);
}

/** Browser side: a fresh set of matrix puzzles (images only; the answers stay on the server). */
export async function getMatrixItems(): Promise<MatrixItem[]> {
  const out = await getItems<{ items: MatrixItem[] }>("matrix", "The puzzles couldn't load. Please try again.");
  return out.items ?? [];
}

/** Browser side: a fresh draw of words for one verbal memory run. */
export async function getVerbalMemoryWords(): Promise<string[]> {
  const out = await getItems<{ words: string[] }>("verbal_memory", "The words couldn't load. Please try again.");
  return out.words ?? [];
}

/** Browser side: a fresh seeded item set for one run of a seeded game. */
export async function getSeededItems<T>(
  test:
    | "mental_math"
    | "mental_rotation"
    | "trail_making"
    | "symbol_digit"
    | "balloon_risk"
    | "iowa_gambling"
    | "tower_of_london"
    | "sense_of_direction",
): Promise<T> {
  return getItems<T>(test, "The test couldn't load. Please try again.");
}

/** Browser side: this week's round of "guess 2/3 of the average" and last week's result. */
export async function getTwoThirdsState(): Promise<TwoThirdsState> {
  return getItems<TwoThirdsState>("two_thirds", "The game couldn't load. Please try again.");
}

/**
 * Browser side: the text (typing) or targets (aim trainer) of one run; with
 * `seed` (a friend's challenge link) the same ones the friend got.
 */
export async function getSkillItems<T>(test: "typing_speed" | "aim_trainer", seed?: number | null): Promise<T> {
  return getItems<T>(test, "The test couldn't load. Please try again.", seed);
}

/** Browser side: today's Helibrain Daily (the same for everyone) and today's numbers. */
export async function getDailyPuzzle(): Promise<DailyPuzzleItems> {
  return getItems<DailyPuzzleItems>("daily_puzzle", "Today's puzzle couldn't load. Please try again.");
}

/** Browser side: a passage and its questions for one reading speed run (no answers). */
export async function getReadingItems(): Promise<ReadingSpeedItems> {
  return getItems<ReadingSpeedItems>("reading_speed", "The text couldn't load. Please try again.");
}

/**
 * Browser side: a run of one of the brain-area games (set G): vocabulary and
 * task switching get a fresh seed; the two daily games get today's set.
 */
export async function getAreaGameItems<T>(
  test: "vocabulary" | "task_switching" | "knowledge_quiz" | "remote_associates",
): Promise<T> {
  return getItems<T>(test, "The game couldn't load. Please try again.");
}

/** Browser side: a fresh run of the IQ-style test (seed + every section's items; no answers). */
export async function getIqItems(): Promise<IqItems> {
  return getItems<IqItems>("iq", "The test couldn't load. Please try again.");
}

/** Browser side: the statements of a questionnaire in the page's language. */
export async function getQuestionnaireItems(test: "big_five" | "imagery"): Promise<QuestionnaireItems> {
  return getItems<QuestionnaireItems>(test, "The test couldn't load. Please try again.");
}

/** Browser side: which of the typed words would count in the creativity test. */
export async function checkCreativityWords(words: string[]): Promise<WordCheck[]> {
  const out = await postJson<{ results: WordCheck[] }>("/free_tests/creativity/check_words", { words, locale: testLocale });
  return out.results ?? [];
}
